import { inject, Injectable } from '@angular/core';
import { ProductService, SqliteService } from '@app/services';
import { ProductCategoryModel, ProductModel } from '@app/models';
import { ProductCategoryRepository } from './product-category.repository';
import { UserRepository } from '@app/repositories/user.repository';

@Injectable({
    providedIn: 'root',
})
export class ProductRepository
{
    private readonly keys = {
        tableName: 'products',
        productCategoryTableName: 'product_categories',
        priceLIstTableName: 'price_lists',
        preorderedTableName: 'preordered_items',
    };
    private sqliteService = inject(SqliteService);
    private productService = inject(ProductService);
    private userRepository = inject(UserRepository);
    private productCategoryRepository = inject(ProductCategoryRepository);

    async downloadProducts()
    {
        const products = await this.productService.getProducts();

        if (products.length > 0)
        {
            await this.saveProducts(products);
        }

        return products;
    }

    async saveProducts(products: ProductModel[]): Promise<void>
    {
        const productCategories: ProductCategoryModel[] = products
            .filter((p) => p.product_category !== null)
            .map((p) => p.product_category!);
        await this.productCategoryRepository.saveProductCategories(productCategories);

        const columns = [
            'id',
            'product_category_id',
            'external_id',
            'name',
            'barcode',
            'image_path',
            'price',
            'sort_order',
            'bu',
        ];
        const batchSize = Math.floor(3000 / columns.length);

        for (let i = 0; i < products.length; i += batchSize)
        {
            const chunk = products.slice(i, i + batchSize);
            const valuePlaceholders = this.sqliteService.getValuePlaceholder(chunk.length, columns.length);
            const sql = `
                INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
                VALUES ${valuePlaceholders} ON CONFLICT(id) DO
                UPDATE
                    SET
                        sort_order = excluded.sort_order,
                    product_category_id = excluded.product_category_id,
                    external_id = excluded.external_id,
                    barcode = excluded.barcode,
                    price = excluded.price,
                    bu = excluded.bu,
                    name = excluded.name;
            `;
            const values: any[] = chunk.flatMap((p) => [
                p.id,
                p.product_category_id,
                p.external_id,
                p.name,
                p.barcode,
                p.image_path,
                p.price,
                p.sort_order,
                p.bu,
            ]);
            await this.sqliteService.save(sql, values);
        }
    }

    async getProducts(filterPriceLists?: {
        routeExternalId: string,
        storeExternalId: string,
    }): Promise<ProductModel[]>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return [];
        }

        let sql = `
            SELECT ${this.keys.tableName}.*,
                   pc.id          as category_id,
                   pc.name        as category_name,
                   pc.external_id as category_external_id
        `;
        const values: any[] = [];

        if (filterPriceLists)
        {
            sql += `,
               COALESCE(
                   (SELECT pl0.price
                    FROM ${this.keys.preorderedTableName} pl0
                    WHERE DATE(pl0.created_at) = DATE('now')
                      AND pl0.product_external_id = ${this.keys.tableName}.external_id
                      AND pl0.route_external_id = ?
                      AND pl0.store_external_id = ?
                    LIMIT 1),
                   (SELECT pl1.price
                    FROM ${this.keys.priceLIstTableName} pl1
                    WHERE pl1.product_external_id = ${this.keys.tableName}.external_id
                      AND pl1.route_external_id = ?
                      AND pl1.store_external_id = ?
                    LIMIT 1),
                   (SELECT pl2.price
                    FROM ${this.keys.priceLIstTableName} pl2
                    WHERE pl2.product_external_id = ${this.keys.tableName}.external_id
                      AND pl2.route_external_id = ?
                      AND pl2.store_external_id IS NULL
                    LIMIT 1),
                    ${this.keys.tableName}.price
               ) AS price
        `;

            values.push(
                filterPriceLists.routeExternalId,
                filterPriceLists.storeExternalId,
                filterPriceLists.routeExternalId,
                filterPriceLists.storeExternalId,
                filterPriceLists.routeExternalId,
            );
        }

        sql += `
           FROM ${this.keys.tableName}
                     LEFT JOIN ${this.keys.productCategoryTableName} pc ON ${this.keys.tableName}.product_category_id = pc.id
        `;

        if (user.bu !== null)
        {
            sql += `
            WHERE ${this.keys.tableName}.bu = ?
        `;

            values.push(user.bu);
        }

        sql += `
        ORDER BY ${this.keys.tableName}.sort_order IS NULL, ${this.keys.tableName}.sort_order, ${this.keys.tableName}.name ASC;
        ;`
        const data = await this.sqliteService.get(sql, values) ?? [];

        return data
            .filter((row: any) => filterPriceLists ? row.price !== null : true)
            .map((row: any) => ({
                id: row.id,
                product_category_id: row.product_category_id,
                external_id: row.external_id,
                name: row.name,
                barcode: row.barcode,
                image_path: row.image_path,
                price: row.price,
                sort_order: row.sort_order,
                bu: row.bu,
                product_category: row.category_id
                    ? {
                        id: row.category_id,
                        name: row.category_name,
                        external_id: row.category_external_id
                    } as ProductCategoryModel
                    : null,
            })) as ProductModel[];
    }

    async getProductById(
        id: number,
        filterPriceLists?: {
            routeExternalId: string,
            storeExternalId: string,
        },
    ): Promise<ProductModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        let sql = `
        SELECT ${this.keys.tableName}.*,
               pc.id          as category_id,
               pc.name        as category_name,
               pc.external_id as category_external_id
    `;
        const values: any[] = [];

        if (filterPriceLists)
        {
            sql += `,
               COALESCE(
                   (SELECT pl0.price
                    FROM ${this.keys.preorderedTableName} pl0
                    WHERE DATE(pl0.created_at) = DATE('now')
                      AND pl0.product_external_id = ${this.keys.tableName}.external_id
                      AND pl0.route_external_id = ?
                      AND pl0.store_external_id = ?
                    LIMIT 1),
                   (SELECT pl1.price
                    FROM ${this.keys.priceLIstTableName} pl1
                    WHERE pl1.product_external_id = ${this.keys.tableName}.external_id
                      AND pl1.route_external_id = ?
                      AND pl1.store_external_id = ?
                    LIMIT 1),
                   (SELECT pl2.price
                    FROM ${this.keys.priceLIstTableName} pl2
                    WHERE pl2.product_external_id = ${this.keys.tableName}.external_id
                      AND pl2.route_external_id = ?
                      AND pl2.store_external_id IS NULL
                    LIMIT 1),
                    ${this.keys.tableName}.price
               ) AS price
        `;

            values.push(
                filterPriceLists.routeExternalId,
                filterPriceLists.storeExternalId,
                filterPriceLists.routeExternalId,
                filterPriceLists.storeExternalId,
                filterPriceLists.routeExternalId,
            );
        }

        sql += `
        FROM ${this.keys.tableName}
             LEFT JOIN ${this.keys.productCategoryTableName} pc ON ${this.keys.tableName}.product_category_id = pc.id
        WHERE ${this.keys.tableName}.id = ? LIMIT 1;
    `;
        const data = await this.sqliteService.get(sql, [...values, id]);

        if (data === null)
        {
            return null;
        }

        return {
            id: data[0].id,
            product_category_id: data[0].product_category_id,
            external_id: data[0].external_id,
            name: data[0].name,
            barcode: data[0].barcode,
            image_path: data[0].image_path,
            price: data[0].price,
            sort_order: data[0].sort_order,
            bu: data[0].bu,
            product_category: data[0].category_id
                ? {
                    id: data[0].category_id,
                    name: data[0].category_name,
                    external_id: data[0].category_external_id,
                }
                : null,
        };
    }
}
